import { queryOptions } from "@tanstack/react-query";
import type { JSONSchema7 } from "json-schema";
import { rmsClient } from "@/shared/api/clients";
import { getIncidentData, getReportMetadata } from "@/features/reports/api/reportApi";

export const reportKeys = {
  all: ["reports"] as const,
  metadata: (reportId: string) => [...reportKeys.all, "metadata", reportId] as const,
  incident: (reportId: string, version?: number) =>
    [...reportKeys.all, "incident", reportId, version ?? "latest"] as const,
  schema: (reportType?: string) =>
    [...reportKeys.all, "schema", reportType ?? "default"] as const,
};

export function reportMetadataQuery(reportId: string) {
  return queryOptions({
    queryKey: reportKeys.metadata(reportId),
    queryFn: () => getReportMetadata(reportId),
    // Status gates editing/signing, and the sign/save/commit flows don't
    // write to this cache — every mount must be a fresh pending fetch, never
    // a stale first paint from a previous visit.
    staleTime: 0,
    gcTime: 0,
  });
}

export function incidentDataQuery(reportId: string, version?: number) {
  return queryOptions({
    queryKey: reportKeys.incident(reportId, version),
    queryFn: () => getIncidentData(reportId, version),
    // Versioned artifacts are immutable; the latest artifact is mutated by
    // non-querified flows (editor save, sign), so it must not survive
    // navigation — a remount shows the skeleton, then fresh data.
    staleTime: typeof version === "number" ? Infinity : 0,
    gcTime: typeof version === "number" ? 60 * 60_000 : 0,
  });
}

/** RAW, unresolved schema — consumers own their (divergent) $ref pipelines. */
export function reportSchemaQuery(reportType?: string) {
  return queryOptions<JSONSchema7>({
    queryKey: reportKeys.schema(reportType),
    queryFn: async () => {
      const response = await rmsClient.get(
        "/schema",
        reportType ? { params: { reportType } } : {},
      );
      return response.data.schema as JSONSchema7;
    },
    staleTime: Infinity,
    gcTime: 60 * 60_000,
  });
}
